import { useEffect, useState } from "react";
import axios from "axios";
import { Box, useThemeUI } from "theme-ui";
import { useSelector } from "react-redux";
import {
  filter,
  flow,
  get,
  getOr,
  has,
  isString,
  map,
  update,
} from "lodash/fp";
import DOMPurify from "dompurify";

import { createImage, fetchInventoryForUserChoices } from "api";
import {
  addToOrder,
  displayReviewModal,
  displaySaveModal,
  displayShareModal,
  displaySimilarOptionsModal,
} from "store/app/appSlice";
import {
  selectCustomisationNotifications,
  selectSelectedProduct,
  selectProductCost,
  selectIsOutOfStock,
  selectAddToOrderValues,
  selectHasAllRequiredValues,
  selectProductImageAngles,
  selectProductImagesForAngle,
} from "store/app";
import {
  Button,
  LoadingIndicator,
  MobileMenu,
  Notifications,
  PersonalizationName,
} from "components";
import ActionsBarMobile from "./ActionsBarMobile";
import AddToOrder from "./AddToOrder";
import ProductDetails from "./ProductDetails";
import CustomisationsContainer from "./CustomisationsContainer";
import ProductImageContainer from "./ProductImageContainer";
import { HelpIcon } from "components/icons";
import { getParamsForFetchInventory } from "utils";
import * as styles from "./Product.styles";

export default function Product({
  isAddingProductToOrder,
  customisations,
  changeProduct,
  selectedCustomisationId,
  userChoiceValues,
  selectedImageViewAngle,
  imageViewAnglesObj,
  showChangeImageAngleArrowButtons,

  dispatchSetCustomisationId,
  dispatchSetSelectedImageAngle,
  dispatch,
}) {
  const {
    theme: { isMobile, isDesktop },
  } = useThemeUI();

  const products = useSelector(getOr([], ["app", "formData", "products"]));
  const product = useSelector(selectSelectedProduct);

  const [inventoryResponse, setInventoryResponse] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const cost = useSelector(selectProductCost);
  const standaloneLearnPage = product?.standaloneLearnPage;

  const { playerName, playerNumber, engineeredFor } = useSelector(
    getOr({}, ["app", "userChoiceValues"])
  );

  // Find notifications based on isOutOfStock status
  const notifications = useSelector(selectCustomisationNotifications);

  // Filter notifications based on type (product or customisation)
  const productNotifications = notifications.filter(
    (notification) => notification.type === "product"
  );

  const customisationNotifications = notifications.filter(
    (notification) => notification.type === "customisation"
  );

  const dispatchDisplayReviewModal = () => dispatch(displayReviewModal());
  const dispatchDisplaySaveModal = () => dispatch(displaySaveModal());
  const dispatchDisplayShareModal = () => dispatch(displayShareModal());
  const dispatchSimilarOptionsModal = () =>
    dispatch(
      displaySimilarOptionsModal({
        modalProps: {
          title: product?.seeSimilarOptions?.content?.title,
          text: inventoryResponse?.recommendations,
        },
      })
    );

  // Manual out of stock from JSON
  const isOutOfStock = useSelector(selectIsOutOfStock);

  // API out of stock
  const inventoryIsOutOfStock = inventoryResponse?.isOutOfStock;

  const addToOrderValues = useSelector(selectAddToOrderValues);
  const hasAllRequiredValues = useSelector(selectHasAllRequiredValues);

  useEffect(() => {
    if (hasAllRequiredValues && product?.seeSimilarOptions) {
      const controller = new AbortController();
      const fetchInventory = fetchInventoryForUserChoices({
        signal: controller.signal,
      });

      setInventoryResponse({
        isOutOfStock: null,
        recommendations: null,
        productData: null,
      });

      fetchInventory({ addToOrderValues })
        .then(({ data }) => {
          const parser = new DOMParser();
          const originalDoc = parser.parseFromString(data, "text/html");

          const script = originalDoc.querySelector("#quickturnProductData");
          if (!script) throw new Error("Missing #quickturnProductData");

          let rawJson = script ? script.textContent.trim() : null;

          // Optional: fix Shopify malformed JSON if needed
          rawJson = rawJson.replace(
            /("handle"\s*:\s*"[^"]*")(\s*"[\d]+")/,
            "$1,$2"
          );

          const sanitizedHtml = DOMPurify.sanitize(data);
          const doc = parser.parseFromString(sanitizedHtml, "text/html");

          const recommendations = doc.querySelector(
            "#alternate-product-recommendations"
          );

          try {
            const productData = JSON.parse(rawJson);

            // Find all numeric keys (likely variant IDs)
            const variantKeys = Object.keys(productData).filter(
              (key) => !isNaN(key)
            );
            const variantId = variantKeys[0];

            const isOutOfStock = variantKeys.some(
              (key) => productData[key].available === "false"
            );

            const recommendationsHtml = recommendations
              ? recommendations.outerHTML
              : null;

            setInventoryResponse({
              isOutOfStock,
              recommendations: recommendationsHtml,
              productData,
              variantId,
            });
          } catch (jsonError) {
            throw new Error(
              `Error fetching for product handle: ${getParamsForFetchInventory({
                addToOrderValues,
              })}`
            );
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            console.error(error);
            setInventoryResponse((prev) => {
              return {
                ...prev,
                isOutOfStock: true,
                recommendations: null,
                productData: null,
                variantId: null,
              };
            });
          }
        });
      return () => controller.abort();
    }
  }, [JSON.stringify(addToOrderValues), hasAllRequiredValues]);

  // Handle add to order here as well as review modal
  const [promise, setPromise] = useState(null);

  const imageAngles = useSelector(selectProductImageAngles);

  const productImagesForAngle = useSelector(
    selectProductImagesForAngle(imageAngles[0])
  );

  const filteredProductImages = productImagesForAngle.map(
    ({ imageSet, images }) => ({
      imageSet,
      images: images.filter(has("url")),
    })
  );

  const images = filteredProductImages.map(
    update("images", flow(map(get("url")), filter(isString)))
  );

  const inventoryParams = product?.seeSimilarOptions
    ? getParamsForFetchInventory({ addToOrderValues })
    : null;

  const dispatchAddToOrder = () => {
    const _promise = dispatch(
      addToOrder({
        images,
        ...addToOrderValues,
        productHandle: inventoryParams,
        variantId: inventoryResponse.variantId,
      })
    );
    setPromise(_promise);
  };

  // cancel any fetch promises in progress, on component unmount
  useEffect(() => {
    return () => {
      if (promise) {
        promise.abort("cancelled");
      }
    };
  }, [promise?.requestId]);

  useEffect(() => {
    const controller = new AbortController();

    images.map((image) =>
      createImage({ signal: controller.signal })({
        images: image.images,
      })
    );

    return () => {
      controller.abort();
    };
  }, [JSON.stringify(images)]);

  return (
    <>
      {isAddingProductToOrder && (
        <Box sx={styles.loadingIndicatorContainer}>
          <LoadingIndicator />
        </Box>
      )}

      {isMobileMenuOpen && (
        <MobileMenu
          gameFitDealerLink={product?.gameFitDealerLink}
          dispatchDisplaySaveModal={dispatchDisplaySaveModal}
          dispatchDisplayShareModal={dispatchDisplayShareModal}
          toggleMobileMenu={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
        />
      )}

      <Box sx={styles.gridLayout}>
        <Box sx={styles.productContainer}>
          <Box sx={styles.productDetailsContainer}>
            <ProductDetails
              product={product}
              cost={cost}
              standaloneLearnPage={standaloneLearnPage}
              selectedImageViewAngle={selectedImageViewAngle}
              imageViewAnglesObj={imageViewAnglesObj}
              setSelectedImageAngle={dispatchSetSelectedImageAngle}
              showChangeButton={products.length > 1}
              changeProduct={changeProduct}
            />
          </Box>

          {/* Show product notifications when isOutOfStock is true */}
          {isOutOfStock && productNotifications.length > 0 && (
            <Box sx={styles.notificationsContainer}>
              <Notifications notifications={productNotifications} canClose />
            </Box>
          )}

          {!isMobile &&
            !isOutOfStock &&
            customisationNotifications.length > 0 && (
              <Box sx={styles.notificationsContainer}>
                <Notifications
                  notifications={customisationNotifications}
                  canClose
                />
              </Box>
            )}

          <ProductImageContainer
            selectedImageViewAngle={selectedImageViewAngle}
            imageViewAnglesObj={imageViewAnglesObj}
            setSelectedImageAngle={dispatchSetSelectedImageAngle}
            showChangeImageAngleArrowButtons={showChangeImageAngleArrowButtons}
          />

          {isDesktop && (
            <AddToOrder
              seeSimilarOptions={product?.seeSimilarOptions}
              isOutOfStock={isOutOfStock || inventoryIsOutOfStock}
              inventoryResponse={inventoryResponse}
              gameFitDealerLink={product?.gameFitDealerLink}
              dispatchAddToOrder={dispatchAddToOrder}
              dispatchDisplaySaveModal={dispatchDisplaySaveModal}
              dispatchDisplayShareModal={dispatchDisplayShareModal}
              dispatchSimilarOptionsModal={dispatchSimilarOptionsModal}
            />
          )}

          <Box sx={styles.personalizationNameContainer}>
            <PersonalizationName
              playerName={playerName || (product?.name || "").split(" ")[0]}
              playerNumber={
                playerName
                  ? playerNumber || ""
                  : (product?.name || "").split(" ")[1]
              }
              engineeredFor={engineeredFor || product?.subName || ""}
            />
          </Box>
        </Box>

        <CustomisationsContainer
          customisations={customisations}
          selectedCustomisationId={selectedCustomisationId}
          userChoiceValues={userChoiceValues}
          dispatchSetCustomisationId={dispatchSetCustomisationId}
          dispatchDisplayReviewModal={dispatchDisplayReviewModal}
        />

        {!isDesktop && (
          <Box sx={styles.bottomBarContainer}>
            <ActionsBarMobile
              isOutOfStock={isOutOfStock || inventoryIsOutOfStock}
              dispatchAddToOrder={dispatchAddToOrder}
              displayReviewModal={dispatchDisplayReviewModal}
              toggleMobileMenu={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              selectedImageViewAngle={selectedImageViewAngle}
              imageViewAnglesObj={imageViewAnglesObj}
              setSelectedImageAngle={dispatchSetSelectedImageAngle}
            />
          </Box>
        )}

        {!isDesktop &&
          hasAllRequiredValues &&
          product.seeSimilarOptions &&
          inventoryResponse?.isOutOfStock &&
          inventoryResponse?.recommendations && (
            <Button
              variant="secondary"
              isRound
              isSelected
              iconBefore={<HelpIcon />}
              size="medium"
              onClick={dispatchSimilarOptionsModal}
              sx={styles.seeSimilarOptionsButton}
            >
              {product.seeSimilarOptions.content.buttonText}
            </Button>
          )}
      </Box>
    </>
  );
}
